import { Patient, Reference } from '@medplum/fhirtypes';

/** The processor exports the Imports view reads (BAR-3028). */
export type ImportSource = 'CHERRY' | 'CARECREDIT' | 'PATIENTFI';

export const IMPORT_SOURCES: { value: ImportSource; label: string; dropTitle: string; accept: string }[] = [
  { value: 'CHERRY', label: 'Cherry', dropTitle: 'Cherry transactions export', accept: '.csv' },
  { value: 'CARECREDIT', label: 'CareCredit', dropTitle: 'CareCredit settlement report', accept: '.xlsx' },
  { value: 'PATIENTFI', label: 'PatientFi', dropTitle: 'PatientFi transactions export', accept: '.csv' },
];

/** The bot's limit, and the payments backend's. */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

export type ImportSkipReason = 'imported' | 'webhook' | 'not_completed' | 'check' | 'no_payment' | 'no_purchase';

/** Why a row is skipped, on hover. */
export const SKIP_REASON_LABEL: Record<ImportSkipReason, string> = {
  imported: 'Already imported',
  webhook: 'Arrives by webhook',
  not_completed: 'Not completed',
  check: "Doesn't add up; check the processor's portal",
  no_payment: 'The payment it refunds is not on record',
  no_purchase: 'No matching purchase on record',
};

/** A file row as the bot previews it, signed by its effect on cash. */
export interface ImportPreviewRow {
  reference: string;
  kind: 'payment' | 'refund';
  occurredAt: string;
  payerName: string | null;
  patientName: string | null;
  amount: number;
  fee: number | null;
  net: number;
  status: 'new' | 'skip';
  reason: ImportSkipReason | null;
  /** A new payment, which takes a patient; a refund follows its payment's. */
  connectable: boolean;
}

export interface ImportPatientMatch {
  patient: Reference<Patient>;
  name: string;
  exact: boolean;
}

export interface ImportPreviewResult {
  preview: { rows: ImportPreviewRow[]; new: number; skipped: number };
  matches: Record<string, ImportPatientMatch>;
}

/** The patient picked for a new payment. */
export interface ImportPick {
  patient: Reference<Patient>;
  name: string;
}

/** Exact matches arrive connected; the rest wait as suggestions. */
export function initialPicks(matches: Record<string, ImportPatientMatch>): Record<string, ImportPick> {
  return Object.fromEntries(
    Object.entries(matches)
      .filter(([, match]) => match.exact)
      .map(([reference, match]) => [reference, { patient: match.patient, name: match.name }])
  );
}

/** `N new · M skipped · K no patient`, from what's picked now. */
export function previewCounts(
  rows: ImportPreviewRow[],
  picks: Record<string, ImportPick>
): { fresh: number; skipped: number; noPatient: number } {
  return {
    fresh: rows.filter((row) => row.status === 'new').length,
    skipped: rows.filter((row) => row.status === 'skip').length,
    noPatient: rows.filter((row) => row.connectable && !picks[row.reference]).length,
  };
}

/** The bot's `connections`: only new payments that have a patient. */
export function importConnections(
  rows: ImportPreviewRow[],
  picks: Record<string, ImportPick>
): { reference: string; patient: Reference<Patient> }[] {
  return rows
    .filter((row) => row.connectable && picks[row.reference])
    .map((row) => ({ reference: row.reference, patient: picks[row.reference].patient }));
}
